import { useState, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Plus, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EntityList } from '@/widgets/entityList'
import { useLLMsList } from '@/entities/llm'
import {
  useChatbotTests,
  useChatbotTest,
  useChatbotTestStats,
  useDeleteChatbotTest,
  useCloneChatbotTest,
  useTriggerTestRun,
  type ChatbotTest,
} from '@/hooks/useSimulations'
import { TestEditor } from './TestEditor'

type TestStatus = 'all' | 'passed' | 'failed' | 'running' | 'not_run'

function getTestStatus(test: ChatbotTest): Exclude<TestStatus, 'all'> {
  const run = test.test_runs?.[0]
  if (!run) return 'not_run'
  if (run.status === 'running' || run.status === 'pending') return 'running'
  if (run.summary && run.summary.failed === 0 && run.summary.errors === 0) return 'passed'
  return 'failed'
}

const STATUS_DOT: Record<Exclude<TestStatus, 'all'>, string> = {
  not_run: 'bg-gray-300',
  running: 'bg-blue-500 animate-pulse',
  passed: 'bg-green-500',
  failed: 'bg-red-500',
}

function getTestStatusDotClass(test: ChatbotTest): string {
  return STATUS_DOT[getTestStatus(test)]
}

export function SimulationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const { data: llms = [] } = useLLMsList()
  const { data: tests = [], isLoading: testsLoading } = useChatbotTests(null)
  const { data: stats } = useChatbotTestStats()
  const deleteTest = useDeleteChatbotTest()
  const cloneTest = useCloneChatbotTest()
  const triggerRun = useTriggerTestRun()

  const testIdParam = searchParams.get('test_id')
  const isNewTest = testIdParam === 'new'
  const selectedTestId = testIdParam && testIdParam !== 'new' ? parseInt(testIdParam, 10) : null
  const { data: selectedTest, isLoading: testLoading } = useChatbotTest(selectedTestId)

  const [statusFilter, setStatusFilter] = useState<TestStatus>('all')

  // LLM for "Run All"
  const [runAllLlmId, setRunAllLlmId] = useState<number | null>(null)
  const effectiveLlmId = runAllLlmId ?? llms[0]?.id ?? null

  const confirmUnsavedChanges = useCallback((message: string): boolean => {
    if (!hasUnsavedChanges) return true
    return window.confirm(message)
  }, [hasUnsavedChanges])

  const handleSelectTest = useCallback((test: ChatbotTest) => {
    if (!confirmUnsavedChanges('You have unsaved changes. Are you sure you want to leave?')) return
    setHasUnsavedChanges(false)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('test_id', test.id.toString())
    setSearchParams(newParams)
  }, [confirmUnsavedChanges, searchParams, setSearchParams])

  const handleCreateNew = useCallback(() => {
    if (!confirmUnsavedChanges('You have unsaved changes. Create a new test?')) return
    setHasUnsavedChanges(false)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('test_id', 'new')
    setSearchParams(newParams)
  }, [confirmUnsavedChanges, searchParams, setSearchParams])

  const handleDeleteTest = useCallback(async (test: ChatbotTest) => {
    if (!window.confirm('Are you sure you want to delete this test?')) return
    try {
      await deleteTest.mutateAsync(test.id)
      toast.success('Test deleted')
      const newParams = new URLSearchParams(searchParams)
      if (String(selectedTestId) === String(test.id)) newParams.delete('test_id')
      setSearchParams(newParams)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete test')
    }
  }, [deleteTest, selectedTestId, searchParams, setSearchParams])

  const handleSaveSuccess = useCallback((testId: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('test_id', testId.toString())
    setSearchParams(newParams)
  }, [searchParams, setSearchParams])

  const handleCloneTest = useCallback(async (test: ChatbotTest) => {
    try {
      const clone = await cloneTest.mutateAsync(test.id)
      toast.success('Test cloned')
      const newParams = new URLSearchParams(searchParams)
      newParams.set('test_id', clone.id.toString())
      setSearchParams(newParams)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to clone test')
    }
  }, [cloneTest, searchParams, setSearchParams])

  const filteredTests = statusFilter === 'all'
    ? tests
    : tests.filter(t => getTestStatus(t) === statusFilter)

  const handleRunAll = useCallback(async () => {
    if (!effectiveLlmId || filteredTests.length === 0) return
    for (const test of filteredTests) {
      try {
        await triggerRun.mutateAsync({ testId: test.id, llmId: effectiveLlmId })
      } catch (error) {
        toast.error(`Failed to run test "${test.name}"`)
      }
    }
    toast.success(`Started ${filteredTests.length} test run(s)`)
  }, [effectiveLlmId, filteredTests, triggerRun])

  const STATUS_ITEMS = [
    { label: 'Passed', count: stats?.passed ?? 0, dotClass: 'bg-green-500' },
    { label: 'Running', count: stats?.running ?? 0, dotClass: 'bg-blue-500 animate-pulse' },
    { label: 'Failed', count: stats?.failed ?? 0, dotClass: 'bg-red-500' },
    { label: 'Not Run', count: stats?.not_run ?? 0, dotClass: 'bg-gray-300' },
  ]

  return (
    <div className="w-full h-full p-6 overflow-hidden flex flex-col">
      <div className="mb-4 flex-shrink-0">
        <h1 className="text-heading font-bold text-gray-900 mb-1">Simulations</h1>
        <p className="text-detail text-gray-600">
          Create and run automated simulations for your LLMs
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-0">
        {/* Left Panel */}
        <EntityList<ChatbotTest>
          items={filteredTests}
          selectedId={selectedTestId ?? undefined}
          getId={(t) => t.id}
          onSelect={handleSelectTest}
          renderPrimary={(t) => (
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getTestStatusDotClass(t)}`} />
              {t.name}
            </div>
          )}
          renderSecondary={(t) =>
            t.description ? (
              <div className="text-detail text-gray-500 truncate mt-0.5">{t.description}</div>
            ) : null
          }
          onDuplicate={handleCloneTest}
          onDelete={handleDeleteTest}
          searchPlaceholder="Search tests..."
          searchFilter={(t, q) =>
            t.name.toLowerCase().includes(q) ||
            (t.description ?? '').toLowerCase().includes(q)
          }
          createButton={
            <div className="space-y-2">
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as TestStatus)}>
                <SelectTrigger className="h-8 text-detail">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tests ({tests.length})</SelectItem>
                  <SelectItem value="passed">Passed ({stats?.passed ?? 0})</SelectItem>
                  <SelectItem value="failed">Failed ({stats?.failed ?? 0})</SelectItem>
                  <SelectItem value="running">Running ({stats?.running ?? 0})</SelectItem>
                  <SelectItem value="not_run">Not Run ({stats?.not_run ?? 0})</SelectItem>
                </SelectContent>
              </Select>
              {stats && (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 pb-2 border-b border-gray-200">
                  {STATUS_ITEMS.map(({ label, count, dotClass }) => (
                    <div key={label} className="flex items-center gap-1.5">
                      <div className={`w-2 h-2 rounded-full ${dotClass}`} />
                      <span className="text-detail text-gray-500">{label}: {count}</span>
                    </div>
                  ))}
                </div>
              )}
              <Select
                value={effectiveLlmId ? String(effectiveLlmId) : ''}
                onValueChange={(v) => setRunAllLlmId(Number(v))}
              >
                <SelectTrigger className="h-8 text-detail">
                  <SelectValue placeholder="Select LLM" />
                </SelectTrigger>
                <SelectContent>
                  {llms.map((llm) => (
                    <SelectItem key={llm.id} value={String(llm.id)}>
                      {llm.name || `LLM #${llm.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="w-full"
                onClick={handleRunAll}
                disabled={triggerRun.isPending || filteredTests.length === 0 || !effectiveLlmId}
              >
                <Play className="w-3 h-3 mr-1.5" />
                {triggerRun.isPending ? 'Running...' : 'Run All Tests'}
              </Button>
              <Button variant="outline" size="sm" className="w-full" onClick={handleCreateNew}>
                <Plus className="w-3 h-3 mr-2" />
                Create New Test
              </Button>
            </div>
          }
          isLoading={testsLoading}
          emptyMessage="No tests yet"
        />

        {/* Right Panel */}
        <div className="flex-1 overflow-y-auto">
          {testLoading ? (
            <div className="h-48 flex items-center justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : (selectedTestId || isNewTest) ? (
            <TestEditor
              test={selectedTest ?? null}
              llms={llms}
              defaultLlmId={effectiveLlmId}
              isNew={isNewTest}
              onChange={setHasUnsavedChanges}
              onSaveSuccess={handleSaveSuccess}
            />
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <p className="text-body">Select a test to start editing or create a new one</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}